import {
	ApolloClient,
	ApolloLink,
	ApolloProvider,
	concat,
	from,
	HttpLink,
	InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import axios, { AxiosRequestConfig } from 'axios';
import { SERVER_URL } from 'Constants';
import { createBrowserHistory } from 'history';
import { BusinessNumberDescriptionProvider } from 'Hooks/ApiContext/BusinessNumberDesciptionContext';
import Cookies from 'js-cookie';
import { configure } from 'mobx';
import { Provider } from 'mobx-react';
import { store, StoreContext } from 'Models/Store';
import * as React from 'react';
import { Route, Router, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { configureAuthenticator2fa } from 'Services/TwoFactor/Authenticator';
import { TwoFactorContext, TwoFactorMethods } from 'Services/TwoFactor/Common';
import { configureEmail2fa } from 'Services/TwoFactor/Email';
import { isServerError } from 'Util/GraphQLUtils';
import { logout } from 'Util/NavigationUtils';
import GlobalModal from './Views/Components/Modal/GlobalModal';
import { ApplicationProvider } from 'Hooks/ApiContext/ApplicationContext';
const Admin = React.lazy(() => import('Views/Admin'));
const Frontend = React.lazy(() => import('Views/Frontend'));

export default class App extends React.Component {
	twoFactorMethods: TwoFactorMethods = {};

	constructor(props: any, context: any) {
		super(props, context);

		store.routerHistory = createBrowserHistory();

		store.apolloClient = new ApolloClient({
			link: from([
				this.apolloXsrfLink,
				this.apolloErrorLink,
				concat(new ApolloLink((operation, forward) => {
					// add the authorization to the headers, we allow super users to impersonate organisations
					operation.setContext(({ headers = {} }) => ({
						headers: {
							...headers,
							impersonatingOrganisationId: store.impersonatingOrganisationId,
						},
					}));
					return forward(operation);
				}), new HttpLink({ uri: `${SERVER_URL}/api/graphql` })),
			]),
			cache: new InMemoryCache(),
		});

		// All state changes should be run in an action so enforce that
		configure({ enforceActions: 'observed' });

		// For cross platform mobile responsiveness
		const defineViewportHeight = () => {
			const vh = window.innerHeight * 0.01;
			document.documentElement.style.setProperty('--vh', `${vh}px`);
		};
		defineViewportHeight();
		window.addEventListener('resize', () => defineViewportHeight());

		configureAuthenticator2fa(this.twoFactorMethods);
		configureEmail2fa(this.twoFactorMethods);

		axios.interceptors.request.use((config: AxiosRequestConfig) => {
			config.headers.impersonatingOrganisationId = store.impersonatingOrganisationId;
			return config;
		}, error => {
			// Do something with request error
			return Promise.reject(error);
		});
	}

	public render() {
		return (
			<ApolloProvider client={store.apolloClient}>
				<TwoFactorContext.Provider value={this.twoFactorMethods}>
					<StoreContext.Provider value={store}>
						<Provider store={store}>
							<>
								<Router history={store.routerHistory}>
									<ToastContainer className="frontend" />
									<Switch>
										<Route
											path="/admin"
											render={props => (
												<React.Suspense fallback={<></>}>
													<Admin {...props} />
												</React.Suspense>
											)}
										/>
										<Route
											path="/"
											render={props => (
												<React.Suspense fallback={<></>}>
													<BusinessNumberDescriptionProvider>
														<ApplicationProvider>
															<Frontend {...props} />
														</ApplicationProvider>
													</BusinessNumberDescriptionProvider>
												</React.Suspense>
											)}
										/>
									</Switch>
								</Router>
								<GlobalModal
									ref={ref => {
										if (ref) store.modal = ref;
									}}
								/>
							</>
						</Provider>
					</StoreContext.Provider>
				</TwoFactorContext.Provider>
			</ApolloProvider>
		);
	}

	/**
	 * Request handler for the apollo client
	 */
	private apolloXsrfLink = new ApolloLink((operation, next) => {
		operation.setContext({
			headers: {
				'X-XSRF-TOKEN': Cookies.get('XSRF-TOKEN'),
			},
		});
		return next(operation);
	});

	/**
	 * Error Handler for the apollo client
	 */
	private apolloErrorLink = onError(({ networkError }) => {
		if (isServerError(networkError) && networkError.statusCode === 401) {
			logout(store.routerHistory.location.pathname);
		}
	});
}
